/**
 * useOptimizationSession — the Optimization tab's agent, plus the production
 * optimizer run it reasons over and the platform applies it dispatches.
 *
 * Three things the bulk editor's hook does not have to do, because the optimizer
 * is a real pipeline and the applies reach a real ad platform:
 *
 *   1. GET A RUN FIRST. The agent has nothing to talk about until an
 *      full analysis has completed. This hook resolves eligibility, restores
 *      the latest explicitly grouped diagnosis/pacing/performance reports, and
 *      otherwise waits for `startRun()`. Only then does it open the agent
 *      session.
 *
 *      `startRun` is NOT called automatically. An optimizer run pulls live
 *      delivery data, costs money, and takes minutes — auto-firing it because
 *      someone clicked a tab is the kind of thing you only get to be wrong about
 *      once. The panel offers a button.
 *
 *   2. DISPATCH THE APPLIES. `confirm_and_apply` does not write to the platform;
 *      it emits `optimization.approved` and this hook then POSTs
 *      `/campaigns/:id/edit` once per suggestion — the same endpoint, and the same
 *      (optimizeMessageId, suggestionIndex) tagging, that the campaign grid and
 *      campaign details page have always used. So the agent sits in FRONT of the
 *      existing apply path rather than adding a second one.
 *
 *   3. TELL THE AGENT WHAT HAPPENED. The edit runs are asynchronous and can fail.
 *      Their outcomes go back via POST /:id/report-apply so the agent's next
 *      answer reflects reality instead of its own last assumption.
 *
 * Platform state:
 *   activePreview       — the proposal awaiting confirmation
 *   lastApprovedBatch   — { apply: [{ index, label, description }] } to dispatch
 *   lastOptimizationFailure — a refused / failed propose or apply, for the banner
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import { useReviewerSession } from './useReviewerSession';
import {
  buildOptimizerDigest,
  extractSuggestions,
} from '../components/CampaignLaunchWorkflow/agent/optimizerDigest';
import { buildSuggestionEditRequest } from '../utils/optimizerSuggestionApply';
import { latestFullAnalysis, combineAnalysisRuns, appliedAnalysisIndexes } from '../utils/sidekickAnalysisRun';
import {
  optimizeEnabledFor,
  optimizeNeedsReportingMetrics,
  optimizeSupportsDestination,
  resolveOptimizePlatformCampaignId,
  startOptimizeRun,
  startAllFocusedAnalyses,
  optimizeAnalysesEnabledFor,
  OPTIMIZE_ANALYSES,
  OPTIMIZE_TERMINAL_STATUSES,
} from '../utils/optimizeCampaignRun';

const ROUTE_BASE = '/api/v2/optimization-sessions';
/** Matches CampaignGridPage: must outlast the server-side optimizer timeout. */
const RUN_POLL_TIMEOUT_MS = 125 * 60 * 1000;
const RUN_POLL_INTERVAL_MS = 5000;

/** Phases the panel renders off. */
export const OPT_PHASE = {
  IDLE: 'idle',                 // nothing started
  LOADING: 'loading',           // resolving eligibility + prior runs
  INELIGIBLE: 'ineligible',     // no optimizer, or not live on the platform
  NO_RUN: 'no_run',             // eligible, no completed run to read yet
  RUNNING: 'running',           // an optimizer run is in flight
  READY: 'ready',               // a run is loaded; the agent can work
  ERROR: 'error',
};

/**
 * Can this campaign be optimized, and if not, why — in a sentence the panel can
 * show. Every check here is one the Optimize Campaign button also makes; the
 * difference is that a button can be greyed out with a tooltip while a tab has to
 * explain itself.
 */
function resolveEligibility(facts, accountSettings) {
  const campaign = {
    destination: facts.destination,
    platform_campaign_id: facts.platformCampaignId,
    advertiser_id: facts.advertiserId,
  };
  const platformCampaignId = resolveOptimizePlatformCampaignId(campaign);

  if (facts.error) return { eligible: false, reason: facts.error, platformCampaignId: null };
  if (!facts.platform) {
    return {
      eligible: false,
      reason: `No optimizer for destination "${facts.destination}".`,
      platformCampaignId: null,
    };
  }
  if (!facts.hasOptimizer || !optimizeSupportsDestination(facts.destination)) {
    return {
      eligible: false,
      reason: `${String(facts.destination || facts.platform).toUpperCase()} campaigns have no optimizer — bulk edit works here.`,
      platformCampaignId: null,
    };
  }
  // The account gate — the same `optimize-campaign` setting the button reads. The
  // backend enforces it too at session create; this is what stops the tab
  // offering a Run button that would 403.
  if (accountSettings && !optimizeEnabledFor(campaign, accountSettings)) {
    return {
      eligible: false,
      reason: 'Campaign optimization is not enabled for this platform on this account. '
        + 'An admin can turn it on in the account\u2019s optimize-campaign setting.',
      platformCampaignId,
    };
  }
  if (!platformCampaignId) {
    return {
      eligible: false,
      reason: 'This campaign is not live on the platform yet. Optimization reads real '
        + 'delivery data, so it only runs after launch.',
      platformCampaignId: null,
    };
  }
  // MadHive's optimizer reads an uploaded reporting-metrics ZIP rather than
  // pulling delivery data from an API, so a run needs a file picker the sidebar
  // does not have. Point at the button that does, rather than dispatching a run
  // with nothing to analyse.
  if (optimizeNeedsReportingMetrics(facts.destination)) {
    return {
      eligible: false,
      // Flagged, not just described: a surface that CAN host the upload (the
      // campaign grid, which already has OptimizeCampaignModal wired) offers it
      // rather than leaving the user at a dead end.
      needsReportingMetrics: true,
      reason: 'MadHive optimization needs a reporting-metrics file uploaded first. '
        + 'Start the run with the upload, and I\u2019ll read the results when it finishes.',
      platformCampaignId,
    };
  }
  return { eligible: true, reason: null, platformCampaignId };
}

export function useOptimizationSession({
  accountId,
  userId,
  campaignId,
  destination,
  campaignName,
  // The account's settings map. Optimization is opt-in per platform via the
  // `optimize-campaign` setting, and this is the SAME gate the Optimize Campaign
  // button uses — without it the agent would offer to dispatch a run on an
  // account that never enabled the feature.
  accountSettings = null,
  // The tab is open. Nothing is fetched or started until this is true, so the
  // hook costs nothing on the surfaces where the user never opens it.
  enabled = false,
}) {
  const [phase, setPhase] = useState(OPT_PHASE.IDLE);
  const [context, setContext] = useState(null);
  const [runError, setRunError] = useState(null);
  const [run, setRun] = useState(null);            // { messageId, result, createdAt }
  const [appliedIndexes, setAppliedIndexes] = useState([]);
  const [digest, setDigest] = useState(null);
  const pollTimer = useRef(null);
  const pollGeneration = useRef(0);
  const currentCampaign = useRef(campaignId);
  currentCampaign.current = campaignId;
  // Guards the one-shot context load so a re-render can't restart it.
  const loadedFor = useRef(null);

  const stopPolling = useCallback(() => {
    pollGeneration.current += 1;
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  // Track every child, including accepted jobs after a partial dispatch failure.
  const pollRun = useCallback((members, batchId = null, dispatchError = null) => {
    stopPolling();
    const generation = pollGeneration.current;
    const startedAt = Date.now();
    let polling = false;
    const results = new Map();
    pollTimer.current = setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        await Promise.all(members.map(async (member) => {
          if (OPTIMIZE_TERMINAL_STATUSES.has(results.get(member.messageId)?.status)) return;
          const r = await api.get(getApiUrl(`/api/v2/message/${member.messageId}/status`), {
            params: { accountId, userId },
          });
          results.set(member.messageId, { ...member, status: r.data?.status,
            result: r.data?.result || null, createdAt: Date.now() });
        }));
        if (generation !== pollGeneration.current) return;
        const finished = members.map((member) => results.get(member.messageId));
        if (!finished.every((member) => OPTIMIZE_TERMINAL_STATUSES.has(member?.status))) {
          if (Date.now() - startedAt > RUN_POLL_TIMEOUT_MS) {
            throw new Error('The analysis did not finish in time. Check the campaign details page.');
          }
          return;
        }
        stopPolling();
        const failed = finished.filter((member) => member.status !== 'completed' || !member.result);
        if (dispatchError || failed.length || (batchId && finished.length !== OPTIMIZE_ANALYSES.length)) {
          setRunError(dispatchError || (failed.length
            ? `Analysis incomplete: ${failed.map((m) => `${m.kind || 'optimizer'} (${m.status})`).join(', ')}.`
            : 'Full analysis is missing a report. Run Full Analysis again.'));
          setPhase(OPT_PHASE.ERROR);
          return;
        }
        setRun(batchId ? combineAnalysisRuns(finished, batchId) : finished[0]);
        setPhase(OPT_PHASE.READY);
      } catch (err) {
        if (generation !== pollGeneration.current) return;
        // Retry transient status errors, bounded by the same safety timeout.
        if (Date.now() - startedAt > RUN_POLL_TIMEOUT_MS) {
          stopPolling();
          setRunError(err?.response?.data?.error || err.message);
          setPhase(OPT_PHASE.ERROR);
        }
      } finally {
        polling = false;
      }
    }, RUN_POLL_INTERVAL_MS);
  }, [accountId, userId, stopPolling]);

  // ── Resolve eligibility + the latest run, once per campaign ────────────────
  // The account setting is part of the key: it arrives asynchronously
  // (useAccountSettings), and resolving eligibility once against a null settings
  // map would leave the gate permanently un-applied for the rest of the session.
  const optimizeSetting = accountSettings?.['optimize-campaign'] ?? null;

  // ── A different campaign is a clean slate ─────────────────────────────────
  // The load effect below is async: without this, everything derived from the
  // PREVIOUS campaign — run, digest, applied indexes, eligibility — stayed on
  // screen and in the payload until the new campaign's fetch landed. That is
  // the window in which the action bar offered "Preview all 16" from a campaign
  // the user had already navigated away from, and in which refresh-run could
  // post one campaign's digest into another campaign's session.
  const previousCampaignId = useRef(campaignId);
  useEffect(() => {
    if (previousCampaignId.current === campaignId) return;
    previousCampaignId.current = campaignId;
    stopPolling();
    setRun(null);
    setDigest(null);
    setContext(null);
    setAppliedIndexes([]);
    setRunError(null);
    setPhase(OPT_PHASE.IDLE);
  }, [campaignId, stopPolling]);

  useEffect(() => {
    if (!enabled || !campaignId || !accountId) return;
    const key = `${campaignId}:${accountId}:${optimizeSetting ?? ''}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;

    let cancelled = false;
    (async () => {
      setPhase(OPT_PHASE.LOADING);
      setRunError(null);
      try {
        const ctxRes = await api.get(getApiUrl(`${ROUTE_BASE}/context`), {
          params: { campaignId, accountId, userId },
        });
        if (cancelled) return;
        // The backend supplies the campaign FACTS (destination, the raw
        // platform_campaign_id / advertiser_id columns) and enforces the account
        // gate. The DECISIONS below are taken with the same helpers the Optimize
        // Campaign button uses, so the tab and the button can never disagree about
        // whether a campaign can be optimized.
        const facts = ctxRes.data || {};
        const gate = resolveEligibility(facts, accountSettings);
        setContext({ ...facts, ...gate });
        if (!gate.eligible) {
          setPhase(OPT_PHASE.INELIGIBLE);
          return;
        }

        // Reuse the most recent COMPLETED optimize run rather than starting one.
        const msgRes = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/messages`), {
          params: { accountId, userId },
        });
        if (cancelled) return;
        const messages = msgRes.data?.messages || [];
        if (optimizeAnalysesEnabledFor(facts.destination)) {
          const full = latestFullAnalysis(messages);
          if (!full) {
            setPhase(OPT_PHASE.NO_RUN);
            return;
          }
          if (full.runs.some((member) => !OPTIMIZE_TERMINAL_STATUSES.has(member.status))) {
            setPhase(OPT_PHASE.RUNNING);
            pollRun(full.runs, full.batchId);
            return;
          }
          if (full.runs.length !== OPTIMIZE_ANALYSES.length
            || full.runs.some((member) => member.status !== 'completed' || !member.result)) {
            setRunError('The previous full analysis is incomplete. Run Full Analysis again.');
            setPhase(OPT_PHASE.ERROR);
            return;
          }
          const loaded = combineAnalysisRuns(full.runs, full.batchId);
          setAppliedIndexes(appliedAnalysisIndexes(messages, loaded, {
            destination: facts.destination, platformCampaignId: gate.platformCampaignId,
          }));
          setRun(loaded);
          setPhase(OPT_PHASE.READY);
          return;
        }
        const optimizeRuns = messages
          .filter((m) => m.message_type === 'optimize-campaign' || m.action === 'optimize-campaign')
          .sort((a, b) => new Date(b.message_created_at || b.associated_at)
            - new Date(a.message_created_at || a.associated_at));

        const inFlight = optimizeRuns.find((m) => m.status === 'processing' || m.status === 'pending');
        if (inFlight) {
          setPhase(OPT_PHASE.RUNNING);
          pollRun([{ messageId: inFlight.message_id }]);
          return;
        }
        const latest = optimizeRuns.find((m) => m.status === 'completed');
        if (!latest) {
          setPhase(OPT_PHASE.NO_RUN);
          return;
        }
        // Which of the run's suggestions a completed edit already applied — the
        // campaign details page's matcher, which reads the tag off the USER
        // message (source_file_metadata); the bot row never carries it.
        setAppliedIndexes(appliedAnalysisIndexes(messages, { messageId: latest.message_id, result: latest.json_response }, {
          destination: facts.destination,
          platformCampaignId: gate.platformCampaignId,
        }));
        setRun({
          messageId: latest.message_id,
          result: latest.json_response || null,
          createdAt: latest.message_created_at || latest.associated_at || null,
        });
        setPhase(OPT_PHASE.READY);
      } catch (err) {
        if (cancelled) return;
        setRunError(err?.response?.data?.error || err.message);
        setPhase(OPT_PHASE.ERROR);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, campaignId, accountId, userId, optimizeSetting, pollRun]);

  // ── Digest — the agent's whole view of the run ─────────────────────────────
  useEffect(() => {
    if (!run?.result) { setDigest(null); return; }
    const next = buildOptimizerDigest(run.result, { appliedIndexes });
    setDigest({ ...next, platformCampaignId: next.platformCampaignId || context?.platformCampaignId || null });
  }, [run, appliedIndexes, context?.platformCampaignId]);

  /**
   * Run the same full analysis as the campaign controls. Platforms without
   * focused launchers keep their combined optimizer and upload prerequisites.
   */
  const startRun = useCallback(async (customPrompt = null, dateParams = null, reportingMetricsPath = null) => {
    if (!campaignId || !context?.platformCampaignId) return false;
    stopPolling();
    const generation = pollGeneration.current;
    const isCurrent = () => generation === pollGeneration.current && currentCampaign.current === campaignId;
    setRunError(null);
    setRun(null);
    setDigest(null);
    setAppliedIndexes([]);
    setPhase(OPT_PHASE.RUNNING);
    const fullAnalysis = optimizeAnalysesEnabledFor(context.destination);
    try {
      const started = await (fullAnalysis ? startAllFocusedAnalyses : startOptimizeRun)({
        campaignId,
        platformCampaignId: context.platformCampaignId,
        // Only MadHive needs a metrics file, and it is normally refused as
        // ineligible here. CLW's Optimization tab passes one when it hosts the
        // upload through OptimizeCampaignModal; every other caller passes null.
        reportingMetricsPath,
        // What the user typed into the panel's focus box, if anything.
        customPrompt,
        // Which slice of delivery data to analyse — see utils/optimizeDateRange.
        dateParams,
        accountId,
        userId,
      });
      if (!isCurrent()) return false;
      if (fullAnalysis) {
        pollRun(started.messageIds.map((messageId, index) => ({
          messageId, kind: OPTIMIZE_ANALYSES[index].kind,
        })), started.analysisBatchId);
      } else {
        if (!started.messageId) throw new Error('Optimization started but returned no message id');
        pollRun([{ messageId: started.messageId }]);
      }
      return true;
    } catch (err) {
      if (!isCurrent()) return false;
      const message = err?.response?.data?.error || err.message;
      if (err.messageIds?.length) {
        setRunError(`Full analysis only partly started: ${message}. Waiting for the accepted reports.`);
        pollRun(err.messageIds.map((messageId, index) => ({
          messageId, kind: OPTIMIZE_ANALYSES[index].kind,
        })), err.analysisBatchId, `Full analysis did not fully start: ${message}. Run Full Analysis again.`);
      } else {
        setRunError(message);
        setPhase(OPT_PHASE.ERROR);
      }
      return false;
    }
  }, [campaignId, context, accountId, userId, pollRun, stopPolling]);

  // ── The agent session ─────────────────────────────────────────────────────
  // Memoized so the payload is a stable object between digest changes. An object
  // rebuilt every render would churn useReviewerSession's createSession identity
  // for no reason — harmless today because auto-start does not depend on it, but
  // exactly the kind of thing that becomes a render loop the next time someone
  // adds a dependency.
  const createPayload = useMemo(() => (
    digest && context?.eligible
      ? {
          campaignId,
          campaignName: campaignName || context.campaignName || null,
          // The BACKEND's context is authoritative: it reads the campaign row's
          // own destination. The caller's prop is CLW's channel-picker state,
          // which on a deep-linked campaign is auto-selected to the account's
          // first enabled channel — 'facebook' on a Google Ads campaign — and
          // with the old precedence that mislabel reached agent_sessions
          // (platform 'facebook' on a google-ads-search campaign, rejected by
          // agent_sessions_platform_check) AND would have routed the optimize
          // run to the wrong platform. Prop is only the pre-context fallback.
          destination: context.destination || destination,
          runId: run?.messageId || null,
          runResult: run?.result || null,
          digest,
        }
      : {}
  ), [digest, context, campaignId, campaignName, destination, run]);

  // What the conversation is ABOUT. Changing it tears the session down and
  // opens a new one (see useReviewerSession's sessionKey), and it also gates
  // refresh-run below so a digest can never be posted into a session that was
  // opened for a different campaign or run.
  const sessionKey = campaignId ? `${campaignId}:${run?.messageId || 'no-run'}` : null;

  const session = useReviewerSession({
    accountId,
    userId,
    autoStart: enabled && phase === OPT_PHASE.READY && Boolean(digest),
    createPayload,
    routeBase: ROUTE_BASE,
    logName: 'useOptimizationSession',
    validateCreatePayload: (p) => {
      if (!p?.campaignId) return 'Missing campaignId';
      if (!p?.destination) return 'Missing campaign destination';
      if (!Array.isArray(p?.digest?.suggestions)) return 'No optimization run loaded yet';
      return null;
    },
    isAutoStartReady: (p) => Boolean(
      p?.campaignId && p?.destination && Array.isArray(p?.digest?.suggestions),
    ),
    // A different RUN is a different conversation — indexes are positional, so
    // resuming across runs would leave the agent quoting indexes that now mean
    // something else. Suggestion content changes (an apply landing) go through
    // refresh-run instead of tearing the session down.
    autoStartDeps: [campaignId, run?.messageId],
    // …and these deps alone were not enough, because auto-start bails when a
    // session is already open. Opening a second campaign therefore kept the
    // first campaign's conversation and let refresh-run push the second
    // campaign's digest into it. sessionKey makes the change destructive: the
    // old conversation is torn down and a new one opens for this campaign+run.
    sessionKey,
    initialPlatformState: {
      activePreview: null,
      lastApprovedBatch: null,
      lastOptimizationFailure: null,
    },
    reducePlatformEvent: (data) => {
      if (data.type === 'optimization.preview' && data.preview) {
        return {
          activePreview: { ...data.preview, ts: Date.now() },
          lastOptimizationFailure: null,
        };
      }
      if (data.type === 'optimization.approved') {
        return {
          lastApprovedBatch: {
            preview_id: data.preview_id,
            apply: Array.isArray(data.apply) ? data.apply : [],
            platformCampaignId: data.platform_campaign_id || null,
            ts: Date.now(),
          },
          // The card's job is done — clear it so a stale Confirm can't linger
          // next to an already-dispatched batch.
          activePreview: null,
        };
      }
      if (data.type === 'optimization.failed') {
        return {
          lastOptimizationFailure: {
            tool: data.tool || null,
            error: data.error || 'The optimization operation failed.',
            awaitingApproval: Boolean(data.awaiting_approval),
            ts: Date.now(),
          },
        };
      }
      return null;
    },
  });

  const { sessionId } = session;
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);

  /** Record the user's explicit approval of `previewId`. THE CONFIRM GATE. */
  const approvePreview = useCallback(async (previewId, via = 'click') => {
    if (!sessionId || !previewId) return false;
    setApproving(true);
    setApproveError(null);
    try {
      await api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/approve-preview`), {
        previewId, via, accountId, userId,
      });
      return true;
    } catch (err) {
      setApproveError(err?.response?.data?.error || err.message);
      return false;
    } finally {
      setApproving(false);
    }
  }, [sessionId, accountId, userId]);

  // ── Dispatch an approved batch to the platform ────────────────────────────
  // Keyed on the batch ts so a re-render cannot double-dispatch — which here
  // would mean applying the same change to a live campaign twice.
  const [applying, setApplying] = useState(false);
  const [applyReport, setApplyReport] = useState(null);
  const dispatchedTs = useRef(null);

  useEffect(() => {
    const batch = session.lastApprovedBatch;
    if (!batch || batch.ts === dispatchedTs.current) return;
    if (!batch.apply?.length) return;
    if (!window.confirm(
      `Apply ${batch.apply.length} approved change${batch.apply.length === 1 ? '' : 's'} to the live campaign?`,
    )) return;
    dispatchedTs.current = batch.ts;

    const platformCampaignId = batch.platformCampaignId || context?.platformCampaignId;
    if (!platformCampaignId) {
      setApplyReport({
        ts: batch.ts,
        results: batch.apply.map((a) => ({
          index: a.index, label: a.label, ok: false, error: 'No platform campaign id',
        })),
      });
      return;
    }

    // The edit endpoint needs more than the description the agent saw:
    // targetEntities, and for MadHive the entity chain. Those are platform
    // specifics that live on the RAW suggestion, so they are rebuilt here from
    // the run payload rather than round-tripped through the agent — which has no
    // use for them and would only be another place they could drift.
    const rawSuggestions = extractSuggestions(run?.result?.details);
    // Same precedence as createPayload: the campaign row's destination wins.
    const dest = context?.destination || destination;

    let cancelled = false;
    (async () => {
      setApplying(true);
      const results = [];
      // Sequential, not parallel: these are edit runs against one live campaign,
      // and the backend dispatches each into the same orchestration chat.
      for (const item of batch.apply) {
        const raw = rawSuggestions[item.index];
        const built = raw
          ? buildSuggestionEditRequest(raw, { destination: dest, platformCampaignId })
          : { error: `Suggestion #${item.index} is no longer in the run payload` };
        if (built.error) {
          results.push({ index: item.index, label: item.label, ok: false, error: built.error });
          continue;
        }
        try {
          const res = await api.post(getApiUrl(`/api/v2/campaigns/${campaignId}/edit`), {
            platformCampaignId,
            // The agent's description is what the user actually read on the card,
            // so it wins over a re-derivation of the same string.
            description: item.description || built.description,
            // The validated edits still ride structurally: the description is what the
            // user read, the batch payload is what gets applied.
            ...(built.edits ? { edits: built.edits } : {}),
            targetEntities: built.targetEntities,
            entityContext: built.entityContext,
            suggestedValues: built.suggestedValues,
            accountId,
            userId,
            suggestionIndex: raw.sourceSuggestionIndex ?? item.index,
            optimizeMessageId: raw.sourceMessageId || run?.messageId || null,
          });
          if (!res.data?.success) throw new Error(res.data?.error || 'Failed to create edit message');
          results.push({ index: item.index, label: item.label, ok: true, messageId: res.data.messageId });
        } catch (err) {
          results.push({
            index: item.index,
            label: item.label,
            ok: false,
            error: err?.response?.data?.error || err.message,
          });
        }
      }
      if (cancelled) return;
      setApplying(false);
      setApplyReport({ ts: batch.ts, results });
      // Only the ones that actually dispatched count as applied.
      setAppliedIndexes((prev) => [
        ...new Set([...prev, ...results.filter((r) => r.ok).map((r) => r.index)]),
      ]);
      if (sessionId) {
        api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/report-apply`), {
          results, accountId, userId,
        }).catch((err) => console.warn('[useOptimizationSession] report-apply failed:', err.message));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.lastApprovedBatch]);

  // ── Keep the agent's suggestion view current ───────────────────────────────
  // An apply landing changes which suggestions are still open. Debounced so a
  // burst of dispatches sends one refresh.
  // sessionKey is a dependency, so switching campaign or run cancels a pending
  // refresh instead of delivering the new campaign's digest into the outgoing
  // session — the exact path that had the agent announce, mid-conversation,
  // that "the run has changed under us — this is now a different campaign
  // entirely" while the user was asking it to apply the previous run.
  useEffect(() => {
    if (!sessionId || !digest) return;
    const t = setTimeout(() => {
      api.post(getApiUrl(`${ROUTE_BASE}/${sessionId}/refresh-run`), {
        digest, accountId, userId,
      }).catch((err) => console.warn('[useOptimizationSession] refresh-run failed:', err.message));
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, digest, sessionKey]);

  return {
    ...session,
    // Run lifecycle
    phase,
    context,
    run,
    runError,
    digest,
    startRun,
    // Confirm gate
    approvePreview,
    approving,
    approveError,
    clearApproveError: () => setApproveError(null),
    // Platform applies
    applying,
    applyReport,
  };
}

export default useOptimizationSession;
